import { inject, Injectable } from '@angular/core';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { Navigate } from '@ps-gui/shared-utils';
import { CtBatch, EingangCtBatch, isEingangCtBatch } from '@ps-gui/claims-terminal-shared';

import { buildAuditPatch } from './audit-patch.builder';
import { mapAuditOrderDto } from './audit-order-dto.mapper';
import { mapTenantToAltsystem } from './tenant-altsystem.mapper';
import { CtBatchRedirectStoreService } from './ct-batch-redirect-store.service';
import { PersonenschadenApiService } from '../api/personenschaden-api.service';
import { CtBatchExtractionApiService } from '../api/ct-batch-extraction-api.service';
import { CtBatchTransformationApiService } from '../api/ct-batch-transformation-api.service';
import { CtBatchStoreService } from '../app-state/ct-batch-store.service';
import { PersistDataService } from '../app-state/persist-data.service';
import {
  FullAuditOrderGuiDto,
  OrderResponseDto,
  SplitEntryDto,
  TransformEingangCtBatchDto,
} from '../../models/interfaces';
import { ExtractedDataSnapshot } from '../../models/extracted-values';
import { countForderungsaufstellungen } from '../utils';

/**
 * Implements the FullAuditOrder flow (`ctAuftragZuordnungEnabled` feature). Used by
 * `EingangCtBatchTransmissionService` when the flag is active. Sends one bulk request
 * that creates or assigns the audit and carries the per-split patches; the CT-batch
 * completion is signaled afterward based on the response shape (new audit vs
 * existing-audit selection).
 */
@Injectable({ providedIn: 'root' })
export class AuditOrderTransmissionService {
  private readonly personenschadenApiService = inject(PersonenschadenApiService);
  private readonly ctBatchTransformationApiService = inject(CtBatchTransformationApiService);
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly redirectStore = inject(CtBatchRedirectStoreService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);

  transmitAndRedirect(ctBatch: EingangCtBatch): Observable<unknown> {
    return from(this.createFullOrderRequestDto(ctBatch)).pipe(
      switchMap((dto) =>
        this.personenschadenApiService.performFullAuditOrder(dto).pipe(
          switchMap((response) => {
            // Take the forwarded order — the one whose auditRequestId is filled.
            const audit = response.orderResponses.find(
              (orderResponse) => orderResponse.auditRequestId != null,
            );
            return audit
              ? this.processOrderResponse(audit, ctBatch)
              : throwError(
                  () =>
                    new Error(
                      'FullAuditOrder: keine auditZuordnung mit auditRequestId in der Response',
                    ),
                );
          }),
        ),
      ),
    );
  }

  private async createFullOrderRequestDto(ctBatch: EingangCtBatch): Promise<FullAuditOrderGuiDto> {
    const numberOfCopies = countForderungsaufstellungen(ctBatch) - 1;
    const documents = this.ctBatchStoreService.ctBatch.documents;
    const domain = this.ctBatchStoreService.ctBatchDomain;
    const snapshot0 = await this.persistDataService.getConsolidatedDataSnapshot(0);
    if (isEingangCtBatch(ctBatch)) {
      mapTenantToAltsystem(ctBatch, snapshot0);
    }

    const splitsData: SplitEntryDto[] = [
      {
        splitIndex: -1,
        patchDto: await buildAuditPatch(
          snapshot0,
          domain,
          documents,
          this.persistDataService,
          ctBatch.metaData,
          this.persistDataService.altfallZuweisung?.auftragId ?? '-1',
        ),
      },
    ];

    for (let splitIndex = 0; splitIndex < numberOfCopies; splitIndex++) {
      const snapshot = await this.persistDataService.getConsolidatedDataSnapshot(splitIndex);
      const patchDto = await buildAuditPatch(
        snapshot,
        domain,
        documents,
        this.persistDataService,
        ctBatch.metaData,
        '-1',
      );
      splitsData.push({ splitIndex, patchDto });
    }

    return {
      orderDto: mapAuditOrderDto(snapshot0),
      auditId: this.persistDataService.altfallZuweisung?.auftragId ?? null,
      eingangId: ctBatch.eingangId,
      splitsData,
    };
  }

  private processOrderResponse(
    audit: OrderResponseDto,
    ctBatch: EingangCtBatch,
  ): Observable<unknown> {
    return from(this.persistDataService.getConsolidatedDataSnapshot(0)).pipe(
      switchMap((snapshot) => {
        mapTenantToAltsystem(ctBatch, snapshot);
        return this.ctBatchTransformationApiService.transformEingangCtBatch(
          this.buildEingangTransformDto(ctBatch.eingangId, audit, snapshot),
        );
      }),
      // The just created AuditCtBatch as well as the original CtBatch need to be marked as 'completed'.
      switchMap((auditBatchId) =>
        this.ctBatchExtractionApiService.signalCtBatchCt2Completion(auditBatchId),
      ),
      switchMap(() => this.ctBatchExtractionApiService.signalCtBatchCt2Completion(ctBatch.id)),
      switchMap(() => from(this.redirectAfterOrder(ctBatch, audit.auditId))),
    );
  }

  private buildEingangTransformDto(
    eingangId: string,
    audit: OrderResponseDto,
    snapshot: ExtractedDataSnapshot,
  ): TransformEingangCtBatchDto {
    return {
      eingangId,
      auditId: audit.auditId,
      auditRequestId: audit.auditRequestId ?? '',
      claimNumber: (snapshot.fields['kopfdaten_schadennummer'] as string) ?? '',
      businessTransaction: (snapshot.fields['kopfdaten_kennung'] as string) ?? '',
      tenant: (snapshot.fields['tenant'] as string) ?? '',
      proofType: (snapshot.fields['auftragsart'] as string) ?? '',
      orderDescription: null,
    };
  }

  private async redirectAfterOrder(ctBatch: CtBatch, auditId: string): Promise<void> {
    this.redirectStore.setAuditRedirectUrl(ctBatch.id, auditId);
    await this.persistDataService.reset();
    Navigate.replace(this.redirectStore.getRedirectUrl(ctBatch));
  }
}
